import css from '@emotion/css';
import styled from '@emotion/styled';
import { breakpoints } from 'app/spacing';
import { KizenTypography } from 'app/typography';
import {
  AUDIO_FILE_EXTENSIONS,
  IMAGE_FILE_EXTENSIONS,
} from 'components/FilePicker/constants';
import Icon from 'components/Kizen/Icon';
import { format } from 'date-fns/format';
import useField from 'hooks/useField';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'react-image-resizer';
import { useDebounce, useWindowSize } from 'react-use';
import FileService from 'services/FileService';
import { LabelsPreviewBlock } from './LabelsPreviewBlock';

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
  max-height: calc(100% - 184px); /* 164px D&D button + 20px top margin */

  &:not(:only-child) {
    margin-top: 20px;
  }
  @media screen and (max-width: ${breakpoints.sm}px) {
    &:not(:only-child) {
      margin-top: 15px;
      max-height: calc(100% - 120px); /* 105px D&D button + 15px top margin */
    }
  }
  ${({ isVisible }) =>
    isVisible
      ? ''
      : css`
          opacity: 0;
          pointer-events: none;
        `}
`;

const IMAGE_PREVIEW_WIDTH = 260;

const PreviewImageContainer = styled.div`
  max-width: ${IMAGE_PREVIEW_WIDTH}px;
  max-height: 230px;
  min-width: 180px;
  min-height: 0;
  aspect-ratio: 1;
  height: 100%;
  width: 100%;
  flex-shrink: 2;
  display: flex;
  justify-content: center;
`;

const NoImage = styled.div`
  display: flex;
  height: 100%;
  aspect-ratio: 1;
  background: var(--color-background-light);
  justify-content: center;
  align-items: center;
  > p {
    font-size: 32px;
  }
`;

const StyledPreviewWrapper = styled.div`
  width: 100%;
  order: ${({ order }) => order};
`;

const defaultPreview = {
  name: '',
  url: null,
  size_formatted: '',
  content_type: '',
  created: '',
};

const PreviewBlock = ({
  file,
  updateFile,
  disabled,
  viewable,
  isMobile = false,
}) => {
  const { t } = useTranslation();

  const ext = (file?.name || '').split('.').pop();
  const isImage = IMAGE_FILE_EXTENSIONS.includes(`.${ext.toLowerCase()}`);
  const isAudio = AUDIO_FILE_EXTENSIONS.includes(`.${ext.toLowerCase()}`);

  const [info] = useField(() => {
    return {
      ...defaultPreview,
      ...file,
      created:
        (file?.created &&
          format(new Date(file.created), "M/dd/yyyy 'at' h:mm a")) ||
        '',
    };
  }, [file, file.name]);
  const [size, setSize] = useState(0);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const { height } = useWindowSize();
  const [isVisible, setIsVisible] = useState(false);
  useDebounce(
    () => {
      setSize(containerRef.current?.offsetHeight || 0);
      setIsVisible(
        wrapperRef.current?.offsetHeight === wrapperRef.current?.scrollHeight
      );
    },
    0,
    [height, info]
  );

  return (
    <Wrapper isVisible={isVisible} ref={wrapperRef}>
      {isMobile ? null : (
        <KizenTypography wight="bold">{t('Preview/Info')}</KizenTypography>
      )}
      {!isMobile && (
        <PreviewImageContainer ref={containerRef}>
          {info.url && isImage ? (
            <Image
              key={info.url + size}
              src={info.url}
              height={size}
              width={IMAGE_PREVIEW_WIDTH}
            />
          ) : (
            <NoImage>
              {isAudio ? (
                <Icon
                  icon="volume"
                  size="intrinsic"
                  width="72px"
                  color="var(--color-font-placeholder)"
                />
              ) : (
                ext && (
                  <KizenTypography type="header" weight="bold">
                    {`.${ext.toUpperCase()}`}
                  </KizenTypography>
                )
              )}
            </NoImage>
          )}
        </PreviewImageContainer>
      )}
      {isAudio && info.url && file?.id && !info.deleted && (
        <audio
          controls
          preload="metadata"
          src={FileService.getStreamUrl(file.id)}
          style={{ width: '100%' }}
        />
      )}
      <StyledPreviewWrapper order={size <= 150 ? -1 : 1}>
        <LabelsPreviewBlock
          isMobile={isMobile}
          info={info}
          disabled={disabled}
          file={file}
          updateFile={updateFile}
          viewable={viewable}
          ext={ext}
        />
      </StyledPreviewWrapper>
    </Wrapper>
  );
};
export default PreviewBlock;
